import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { HomeIcon, SearchIcon, CalendarIcon, MessageCircleIcon, GraduationCapIcon, MusicIcon, MenuIcon, XIcon } from 'lucide-react';
import { Navbar } from '../Navbar';
import { Footer } from '../Footer';
import { useTheme } from '../providers/ThemeProvider';
interface StudentLayoutProps {
  children: React.ReactNode;
}
export const StudentLayout: React.FC<StudentLayoutProps> = ({
  children
}) => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const location = useLocation();
  const {
    theme
  } = useTheme();
  const isDark = theme === 'dark';
  // Toggle sidebar on mobile
  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };
  // Open sidebar by default on larger screens
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1024) {
        setIsSidebarOpen(true);
      } else {
        setIsSidebarOpen(false);
      }
    };
    // Set initial state based on screen size
    handleResize();
    // Add event listener
    window.addEventListener('resize', handleResize);
    // Clean up
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);
  return <div className={`w-full min-h-screen ${isDark ? 'bg-gradient-to-r from-[#0F1626] via-[#1E3A8A] to-[#2C3E50] text-white' : 'bg-gradient-to-r from-gray-50 to-gray-100 text-gray-800'} font-sans transition-colors duration-300`} data-id="element-1503">
      <Navbar setIsDeleteAccountModalOpen={() => {}} data-id="element-1504" />
      <div className="pt-20 pb-16 flex flex-col lg:flex-row" data-id="element-1505">
        {/* Mobile sidebar toggle - increased size and fixed positioning */}
        <button onClick={toggleSidebar} className="lg:hidden fixed bottom-4 right-4 z-30 bg-deep-pink text-white p-4 rounded-full shadow-lg" aria-label={isSidebarOpen ? 'Close sidebar' : 'Open sidebar'} style={{
        minWidth: '56px',
        minHeight: '56px'
      }} data-id="element-1506">
          {isSidebarOpen ? <XIcon size={24} data-id="element-1507" /> : <MenuIcon size={24} data-id="element-1508" />}
        </button>
        {/* Left Sidebar - improved mobile layout */}
        <aside className={`w-64 fixed inset-y-0 pt-20 pb-16 ${isDark ? 'bg-navy-900/95' : 'bg-white'} shadow-lg z-20 transform transition-all duration-300 ease-in-out ${isSidebarOpen ? 'translate-x-0' : '-translate-x-full'} lg:translate-x-0`} data-id="element-1509">
          {/* Close button for mobile sidebar */}
          {isSidebarOpen && <button onClick={toggleSidebar} className="lg:hidden absolute top-24 right-4 bg-deep-pink text-white p-3 rounded-full shadow-md" aria-label="Close sidebar" style={{
          minWidth: '48px',
          minHeight: '48px'
        }} data-id="element-1510">
              <XIcon size={20} data-id="element-1511" />
            </button>}
          <nav className="px-4 py-6" data-id="element-1512">
            <ul className="space-y-3" data-id="element-1513">
              <li data-id="element-1514">
                <Link to="/student-dashboard" className={`flex items-center px-4 py-4 ${location.pathname === '/student-dashboard' ? isDark ? 'text-deep-pink bg-navy-800/50' : 'text-deep-pink bg-red-50' : isDark ? 'text-white hover:bg-navy-800/70' : 'text-gray-700 hover:bg-gray-100'} rounded-lg font-medium transition-colors`} onClick={() => window.innerWidth < 1024 && setIsSidebarOpen(false)} data-id="element-1515">
                  <HomeIcon className="h-6 w-6 mr-3" data-id="element-1516" />
                  <span className="text-base" data-id="element-1517">Dashboard</span>
                </Link>
              </li>
              <li data-id="element-1518">
                <Link to="/find-teachers" className={`flex items-center px-4 py-4 ${location.pathname === '/find-teachers' ? isDark ? 'text-deep-pink bg-navy-800/50' : 'text-deep-pink bg-red-50' : isDark ? 'text-white hover:bg-navy-800/70' : 'text-gray-700 hover:bg-gray-100'} rounded-lg font-medium transition-colors`} onClick={() => window.innerWidth < 1024 && setIsSidebarOpen(false)} data-id="element-1519">
                  <SearchIcon className="h-6 w-6 mr-3" data-id="element-1520" />
                  <span className="text-base" data-id="element-1521">Find Teachers</span>
                </Link>
              </li>
              <li data-id="element-1522">
                <Link to="/student-schedule" className={`flex items-center px-4 py-4 ${location.pathname === '/student-schedule' ? isDark ? 'text-deep-pink bg-navy-800/50' : 'text-deep-pink bg-red-50' : isDark ? 'text-white hover:bg-navy-800/70' : 'text-gray-700 hover:bg-gray-100'} rounded-lg font-medium transition-colors`} onClick={() => window.innerWidth < 1024 && setIsSidebarOpen(false)} data-id="element-1523">
                  <CalendarIcon className="h-6 w-6 mr-3" data-id="element-1524" />
                  <span className="text-base" data-id="element-1525">My Schedule</span>
                </Link>
              </li>
              <li data-id="element-1526">
                <Link to="/messages" className={`flex items-center px-4 py-4 ${location.pathname === '/messages' ? isDark ? 'text-deep-pink bg-navy-800/50' : 'text-deep-pink bg-red-50' : isDark ? 'text-white hover:bg-navy-800/70' : 'text-gray-700 hover:bg-gray-100'} rounded-lg font-medium transition-colors`} onClick={() => window.innerWidth < 1024 && setIsSidebarOpen(false)} data-id="element-1527">
                  <MessageCircleIcon className="h-6 w-6 mr-3" data-id="element-1528" />
                  <span className="text-base" data-id="element-1529">Messages</span>
                </Link>
              </li>
              <li data-id="element-1530">
                <Link to="/online-courses" className={`flex items-center px-4 py-4 ${location.pathname === '/online-courses' ? isDark ? 'text-deep-pink bg-navy-800/50' : 'text-deep-pink bg-red-50' : isDark ? 'text-white hover:bg-navy-800/70' : 'text-gray-700 hover:bg-gray-100'} rounded-lg font-medium transition-colors`} onClick={() => window.innerWidth < 1024 && setIsSidebarOpen(false)} data-id="element-1531">
                  <GraduationCapIcon className="h-6 w-6 mr-3" data-id="element-1532" />
                  <span className="text-base" data-id="element-1533">Courses</span>
                </Link>
              </li>
              <li data-id="element-1534">
                <Link to="/sheet-music" className={`flex items-center px-4 py-4 ${location.pathname === '/sheet-music' ? isDark ? 'text-deep-pink bg-navy-800/50' : 'text-deep-pink bg-red-50' : isDark ? 'text-white hover:bg-navy-800/70' : 'text-gray-700 hover:bg-gray-100'} rounded-lg font-medium transition-colors`} onClick={() => window.innerWidth < 1024 && setIsSidebarOpen(false)} data-id="element-1535">
                  <MusicIcon className="h-6 w-6 mr-3" data-id="element-1536" />
                  <span className="text-base" data-id="element-1537">Sheet Music</span>
                </Link>
              </li>
            </ul>
          </nav>
        </aside>
        {/* Main Content - fixed mobile layout */}
        <main className={`flex-1 transition-all duration-300 ease-in-out ${isSidebarOpen ? 'lg:ml-64' : 'ml-0 lg:ml-64'} px-4 py-6 ${isSidebarOpen ? 'md:pl-72' : ''}`} style={{
        minHeight: 'calc(100vh - 144px)'
      }} data-id="element-1538">
          {children}
        </main>
      </div>
      <Footer data-id="element-1539" />
      {/* Overlay for mobile when sidebar is open */}
      {isSidebarOpen && <div className="lg:hidden fixed inset-0 bg-black bg-opacity-50 z-10" onClick={toggleSidebar} aria-hidden="true" data-id="element-1540"></div>}
    </div>;
};